/**
 * Users Migration Search Component
 *
 * Search interface for filtering migration users by email, first name, or last name.
 * Features debounced search input for better performance.
 */

import { useEffect, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

interface UsersMigrationSearchProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

/**
 * Users Migration Search Component
 * Provides a debounced search input for filtering migration users
 */
export function UsersMigrationSearch({
  value,
  onChange,
  placeholder = 'Search by email, first name, or last name...',
}: UsersMigrationSearchProps) {
  const [searchInput, setSearchInput] = useState(value);
  const onChangeRef = useRef(onChange);

  // Keep ref in sync with latest callback
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Sync local state with prop value (only when parent resets search, e.g. tab change)
  useEffect(() => {
    setSearchInput(value);
  }, [value]);

  // Debounce search input — use ref to avoid re-triggering on callback identity changes
  useEffect(() => {
    const timer = setTimeout(() => {
      onChangeRef.current(searchInput);
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [searchInput]);

  /**
   * Handle Clear Search
   */
  const handleClear = () => {
    setSearchInput('');
    onChange('');
  };

  return (
    <div className="relative flex-1 max-w-md">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
      <Input
        value={searchInput}
        onChange={e => setSearchInput(e.target.value)}
        placeholder={placeholder}
        className="pl-9 pr-9"
      />
      {searchInput && (
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClear}
          className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Clear search</span>
        </Button>
      )}
    </div>
  );
}
